import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, User, ShoppingBasket, Menu, X, GitCompareArrows, ShieldCheck } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { useSite } from '../../context/SiteContext';

export const NAV_LINKS = [
  { to: '/', label: 'Home' },
  { to: '/products', label: 'Products' },
  { to: '/products?format=Vial', label: 'Vials' },
  { to: '/products?format=Pen', label: 'Pens' },
  { to: '/products?format=Spray', label: 'Sprays' },
  { to: '/products?format=Bundle', label: 'Bundles' },
  { to: '/compare', label: 'Compare' },
  { to: '/calculator', label: 'Calculator' },
  { to: '/coas', label: 'COAs' },
  { to: '/journal', label: 'Research Journal' },
  { to: '/docs', label: 'Documentation' },
  { to: '/faq', label: 'FAQ' },
  { to: '/contact', label: 'Contact' },
];

export default function Header({ onSearch }) {
  const { count, setDrawerOpen, compare } = useCart();
  const { user, isAdmin } = useAuth();
  const { site } = useSite();
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const h = (e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); onSearch(); } };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onSearch]);

  return (
    <header className="sticky top-0 z-40">
      {site.announcement_bar && (
        <div data-testid="announcement-bar" className="bg-[#0B0F12] text-[#FAFAF8] text-center text-[11px] font-mono2 uppercase tracking-[0.15em] py-1.5 px-4">
          {site.announcement_bar}
        </div>
      )}
      <div className="backdrop-blur-xl bg-[#FAFAF8]/90 border-b hairline">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button data-testid="mobile-menu-button" className="lg:hidden p-2 -ml-2" onClick={() => setMenuOpen(true)} aria-label="Menu">
              <Menu className="w-5 h-5" />
            </button>
            <Link to="/" data-testid="logo-link" className="flex items-baseline gap-2 select-none">
              <span className="font-display font-black text-2xl tracking-tight">VQ HEALTH</span>
              <span className="hidden sm:inline font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#8A8F95]">Research Labs · UK</span>
            </Link>
            <div className="flex items-center gap-1 sm:gap-2">
              <button data-testid="search-button" onClick={onSearch} className="p-2.5 hover:bg-[#F4F4F0] rounded transition-colors" aria-label="Search">
                <Search className="w-5 h-5" />
              </button>
              <Link data-testid="compare-link" to="/compare" className="relative p-2.5 hover:bg-[#F4F4F0] rounded transition-colors" aria-label="Compare">
                <GitCompareArrows className="w-5 h-5" />
                {compare.length > 0 && <span className="absolute -top-0.5 -right-0.5 bg-[#0B0F12] text-white text-[10px] font-mono2 w-4 h-4 flex items-center justify-center rounded-full">{compare.length}</span>}
              </Link>
              <Link data-testid="account-link" to={user ? (isAdmin ? '/admin' : '/account') : '/login'} className="p-2.5 hover:bg-[#F4F4F0] rounded transition-colors" aria-label="Account">
                {isAdmin ? <ShieldCheck className="w-5 h-5" /> : <User className="w-5 h-5" />}
              </Link>
              <button data-testid="basket-button" onClick={() => setDrawerOpen(true)} className="relative p-2.5 hover:bg-[#F4F4F0] rounded transition-colors" aria-label="Basket">
                <ShoppingBasket className="w-5 h-5" />
                {count > 0 && <span data-testid="basket-count" className="absolute -top-0.5 -right-0.5 bg-[#E54D2E] text-white text-[10px] font-mono2 w-4 h-4 flex items-center justify-center rounded-full">{count}</span>}
              </button>
            </div>
          </div>
        </div>
        <nav className="hidden lg:block border-t hairline">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-6 h-11 overflow-x-auto">
              {NAV_LINKS.map((l) => (
                <Link key={l.label} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className="whitespace-nowrap font-mono2 text-[11px] uppercase tracking-[0.14em] text-[#575B60] hover:text-[#0B0F12] transition-colors">
                  {l.label}
                </Link>
              ))}
            </div>
          </div>
        </nav>
      </div>

      {menuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="mobile-menu">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMenuOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-[85%] max-w-sm bg-[#FAFAF8] shadow-xl overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b hairline">
              <span className="font-display font-black text-xl">VQ HEALTH</span>
              <button data-testid="mobile-menu-close" onClick={() => setMenuOpen(false)} className="p-2"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-2">
              {NAV_LINKS.map((l) => (
                <button key={l.label} data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => { setMenuOpen(false); navigate(l.to); }}
                  className="w-full text-left px-4 py-3.5 font-display font-bold uppercase tracking-wide text-lg border-b hairline hover:bg-[#F4F4F0] transition-colors">
                  {l.label}
                </button>
              ))}
              <button data-testid="mobile-nav-account" onClick={() => { setMenuOpen(false); navigate(user ? (isAdmin ? '/admin' : '/account') : '/login'); }}
                className="w-full text-left px-4 py-3.5 font-display font-bold uppercase tracking-wide text-lg text-[#575B60]">
                {user ? (isAdmin ? 'Admin Dashboard' : 'My Account') : 'Sign In'}
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
